"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PAPERWORK_VARIANCE_BAND_YELLOW_MAX = exports.PAPERWORK_VARIANCE_BAND_GREEN_MAX = exports.APosDailyPaperworkSchema = exports.APosPaperworkAuditEventSchema = exports.APosPaperworkAuditActionSchema = exports.APosPaperworkComputedSchema = exports.APosPaperworkDepositSchema = exports.APosPaperworkDropVerificationSchema = exports.APosPaperworkSessionSnapshotSchema = exports.APosPaperworkSalesSnapshotSchema = exports.APosPaperworkActorSchema = exports.APosDailyPaperworkStatusSchema = void 0;
exports.classifyVarianceBand = classifyVarianceBand;
const zod_1 = require("zod");
const id_schema_1 = require("../../common/id.schema");
const pos_register_schema_1 = require("./pos-register.schema");
// ---- Status ----------------------------------------------------------------
exports.APosDailyPaperworkStatusSchema = zod_1.z.enum(['open', 'submitted']);
// ---- Actor (employee or admin touching the record) ------------------------
exports.APosPaperworkActorSchema = zod_1.z.object({
    employeeId: id_schema_1.EntityIdSchema,
    employeeName: zod_1.z.string(),
});
// ---- Snapshots (frozen at key points) --------------------------------------
exports.APosPaperworkSalesSnapshotSchema = zod_1.z.object({
    cashSales: zod_1.z.number().default(0),
    cardSales: zod_1.z.number().default(0),
    splitPaymentCount: zod_1.z.number().int().default(0),
    cashRefunds: zod_1.z.number().default(0),
    paidOuts: zod_1.z.number().default(0),
    cashTipsIn: zod_1.z.number().default(0),
    cardTipsIn: zod_1.z.number().default(0),
    transactionCount: zod_1.z.number().int().default(0),
});
exports.APosPaperworkSessionSnapshotSchema = zod_1.z.object({
    sessionId: id_schema_1.EntityIdSchema,
    registerId: id_schema_1.EntityIdSchema,
    openedBy: exports.APosPaperworkActorSchema,
    closedBy: exports.APosPaperworkActorSchema.optional(),
    openedAt: zod_1.z.string(), // ISO
    closedAt: zod_1.z.string().optional(), // ISO
    startingCash: zod_1.z.number(),
    endingCash: zod_1.z.number().optional(),
    expectedCash: zod_1.z.number().optional(),
    variance: zod_1.z.number().optional(),
    status: zod_1.z.enum(['open', 'closed']),
});
// ---- Verification & deposit ------------------------------------------------
exports.APosPaperworkDropVerificationSchema = zod_1.z.object({
    dropId: id_schema_1.EntityIdSchema,
    verified: zod_1.z.boolean(),
    verifiedNote: zod_1.z.string().max(500).optional(),
    verifiedBy: exports.APosPaperworkActorSchema.optional(),
    verifiedAt: zod_1.z.string().optional(), // ISO
});
exports.APosPaperworkDepositSchema = zod_1.z.object({
    amount: zod_1.z.number().nonnegative(),
    slipRef: zod_1.z.string().min(1).max(200),
    depositedAt: zod_1.z.string(), // ISO — wall-clock time the admin says deposit happened
    notes: zod_1.z.string().max(500).optional(),
    enteredBy: exports.APosPaperworkActorSchema,
    enteredAt: zod_1.z.string(), // ISO — when this form was saved
});
exports.APosPaperworkComputedSchema = zod_1.z.object({
    expectedCash: zod_1.z.number().default(0),
    actualCash: zod_1.z.number().default(0),
    variance: zod_1.z.number().default(0), // expectedCash - actualCash
    varianceDirection: pos_register_schema_1.APosVarianceDirectionSchema.default('even'),
    totalSafeDrops: zod_1.z.number().default(0),
    verifiedSafeDrops: zod_1.z.number().default(0),
});
// ---- Audit event -----------------------------------------------------------
exports.APosPaperworkAuditActionSchema = zod_1.z.enum([
    'created',
    'drop_verified',
    'drop_unverified',
    'deposit_set',
    'variance_note_set',
    'submitted',
    'reopened',
]);
exports.APosPaperworkAuditEventSchema = zod_1.z.object({
    eventId: zod_1.z.string(),
    action: exports.APosPaperworkAuditActionSchema,
    actor: exports.APosPaperworkActorSchema,
    timestamp: zod_1.z.string(), // ISO
    payload: zod_1.z.record(zod_1.z.unknown()).optional(),
});
// ---- The record itself -----------------------------------------------------
exports.APosDailyPaperworkSchema = zod_1.z.object({
    // DynamoDB keys (stored as top-level fields)
    pk: zod_1.z.string(), // "<tenantId>#LOCATION#<locationId>"
    sk: zod_1.z.string(), // "DATE#<yyyy-mm-dd>"
    gsi1pk: zod_1.z.string(), // "<tenantId>"
    gsi1sk: zod_1.z.string(), // "DATE#<yyyy-mm-dd>#LOCATION#<locationId>"
    // Identity
    tenantId: id_schema_1.TenantIdSchema,
    locationId: id_schema_1.EntityIdSchema,
    businessDate: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    status: exports.APosDailyPaperworkStatusSchema,
    // Snapshots
    salesSnapshot: exports.APosPaperworkSalesSnapshotSchema,
    registerSessions: zod_1.z.array(exports.APosPaperworkSessionSnapshotSchema).default([]),
    // Verification & deposit
    dropVerifications: zod_1.z.array(exports.APosPaperworkDropVerificationSchema).default([]),
    deposit: exports.APosPaperworkDepositSchema.optional(),
    // Computed totals
    computed: exports.APosPaperworkComputedSchema,
    // Variance note (required when red band)
    varianceNote: zod_1.z.string().max(1000).optional(),
    // Audit
    audit: zod_1.z.array(exports.APosPaperworkAuditEventSchema).default([]),
    // Submit / reopen lifecycle
    submittedAt: zod_1.z.string().optional(),
    submittedBy: exports.APosPaperworkActorSchema.optional(),
    reopenedAt: zod_1.z.string().optional(),
    reopenedBy: exports.APosPaperworkActorSchema.optional(),
    // Timestamps
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
// ---- Variance band helper (shared between backend and frontend) -----------
exports.PAPERWORK_VARIANCE_BAND_GREEN_MAX = 1; // |variance| <= $1
exports.PAPERWORK_VARIANCE_BAND_YELLOW_MAX = 20; // |variance| <= $20
function classifyVarianceBand(variance) {
    const mag = Math.abs(variance);
    if (mag <= exports.PAPERWORK_VARIANCE_BAND_GREEN_MAX)
        return 'green';
    if (mag <= exports.PAPERWORK_VARIANCE_BAND_YELLOW_MAX)
        return 'yellow';
    return 'red';
}
//# sourceMappingURL=daily-paperwork.schema.js.map